/**
 * FeeStage — the patient link's fee step (patient payments, slice 3a).
 *
 * Rendered by BridgeOnboardingPage ONLY when the bridge answers the status
 * call with a `pending`, unpaused row. The notice wording is the server's
 * (`notice.text_blocks` and the consent sentence `notice.consent_text`), so
 * this component renders it as given and hashes exactly that text
 * (`consentHashInput`) for the consent record. That includes the notice for
 * a physician who charges $0, where Parseo Health bills its own fee ("Dr.
 * {Last} charges nothing for this form…"): the amounts are the push's, never
 * the page's.
 *
 * No free path (ruling 2026-10-14, case imp_a6b6fd85). A row frozen WITHOUT
 * the no-fee choices (`no_fee_choices` not true: every row the Mac sends
 * today) has two ways out:
 *
 *   Continue to payment            needs the consent tick; the pay page opens
 *                                  in a NEW tab (synchronously, in the
 *                                  click), then consent {version,
 *                                  notice_hash, total_cents} → setup → the
 *                                  tab is pointed at the pay URL; this tab
 *                                  waits (PaymentWaiting, whose "Back"
 *                                  returns here and never defers) and
 *                                  continues to the form on `card_saved`
 *   I have decided not to have     never tied to the tick; one confirm
 *   this form filled out           screen, then POST …/decline → `onDecline`
 *   (G20, 2026-10-15)              (the closing page: nothing charged, the
 *                                  office told, no paper form). A 409
 *                                  `not_declinable` on a row still waiting
 *                                  on its fee (the form is already in, or a
 *                                  send is locked in) shows one line
 *                                  pointing to the office; on any other row
 *                                  (the physician closed the request while
 *                                  the page was open, …) it says only that
 *                                  the request changed. A failed post stays
 *                                  here to try again, because nothing was
 *                                  recorded
 *
 * A row frozen WITH the choices (`no_fee_choices: true`, sent before the
 * ruling or with the flag on) keeps today's four ways out, unchanged:
 *
 *   Continue to payment    as above; the waiting view's second button is
 *                          "Continue without paying now", a deferral
 *   I can't pay right now  can't afford / other + note → waiver → the form
 *   I have a question      note → question → the form (the office's phone,
 *                          or Parseo's email when Parseo sells the fee)
 *   Not now                defer → the form (payable later from the link)
 *
 * and on that row every path reaches the questionnaire: a waiver or a
 * question never blocks the form (design D9), and a failed request still
 * lets the patient continue.
 *
 * Who sells the fee is the row's `merchant` (`feeSeller`): the physician's
 * practice, or Parseo Health (the Model A pilot, or a physician who charges
 * $0). Only the chrome around the notice changes with it (the line under the
 * heading, who a waiver or a question goes to, the contact); a physician row
 * renders exactly as it did before Parseo could be the seller.
 *
 * `mode="pay"` is the status page's "Pay the fee": only Continue to payment
 * and Not now, on either kind of row; and, on a row without the choices whose
 * card was declined right before the send (the bridge's `declinable`, G23(c),
 * review of PR #519: nothing was charged and the form was not sent), "I have
 * decided not to have this form filled out" too, with the same confirm
 * screen; `onDecline` then lets the status page show its closing notice. The consent step is skipped only when
 * the bridge reports `consent_current` (the patient already consented to
 * this notice when they saved a card); a row released with no card shows it.
 *
 * A setup refused because the card is in fact already saved (the pay tab
 * finished after the patient pressed Back) re-reads the row and continues as
 * a saved card; a row already declined goes to the closing page; any other
 * row that no longer waits on its fee says the request changed.
 * React-17 safe: no useId, no new dependencies, inline styles.
 */
import React, { useEffect, useRef, useState } from "react";

import { J } from "../../shared/ui/journey/journeyTheme";
import PaymentWaiting, { openPayPage, type PayPageOpened } from "./PaymentWaiting";
import {
  fetchPaymentStatus,
  postPaymentConsent,
  postPaymentDecline,
  postPaymentDefer,
  postPaymentQuestion,
  postPaymentSetup,
  postPaymentWaiver,
  sha256Hex,
  type WaiverReason,
} from "../../lib/bridgeClient";
import {
  DECLINE_BUTTON_LABEL,
  DECLINE_CONFIRM_HEADING,
  DECLINE_CONFIRM_YES,
  DEFERRED_MESSAGE,
  EARLIER_PAYMENT_FAILED_LINE,
  cardSavedStrip,
  consentHashInput,
  contactSentence,
  declineConfirmBody,
  declineFailedLine,
  feeSeller,
  formatCents,
  noFeeChoices,
  notDeclinableLine,
  requestChangedLine,
} from "../../lib/paymentDisplay";
import type { PatientPaymentStatus } from "../../types/shared";

export type FeeOutcome = "card_saved" | "waiver" | "question" | "deferred";

export interface FeeStageProps {
  payment: PatientPaymentStatus;
  /** The patient chose a path that continues to the form; `message` is the
   *  calm confirmation the form screen shows above the questionnaire, and
   *  `latest` the row the server last returned (on `card_saved`). */
  onContinue: (outcome: FeeOutcome, message: string, latest?: PatientPaymentStatus) => void;
  /** The bridge recorded "I have decided not to have this form filled out" (or the row was
   *  already declined): the page shows its closing page. Fee mode, and pay
   *  mode on a row the bridge reports `declinable` (G23(c)). */
  onDecline?: () => void;
  /** Back to the details step (fee mode) / close the panel (pay mode). */
  onBack?: () => void;
  /** "fee" — the link's fee step; "pay" — the status page's Pay the fee. */
  mode?: "fee" | "pay";
  /** Test seams for the waiting view's polling. */
  pollMs?: number;
}

type View = "notice" | "waiver" | "question" | "waiting" | "confirm";

/** Row statuses at which a refused setup means the card is already saved. */
const SAVED_STATUSES = ["card_saved", "paid"];
/** Row statuses the pay page opens on (the bridge's setup: save a card, or
 *  pay now). A refused setup whose row still reads one of them is a passing
 *  failure, and the patient tries again. */
const PAYABLE_STATUSES = ["pending", "deferred", "charge_failed"];
/** Row statuses the bridge's state table lets a decline leave. A decline
 *  refused on one of them was refused by its guard: the form is in, a send
 *  is locked in, or the fee was released, so the form is on its way. A
 *  refusal on any OTHER status means the request changed (the physician
 *  closed it, the fee was waived, …), and "on its way" would be false. */
const DECLINABLE_STATUSES = ["pending", "card_saved", "charge_failed"];

const NOTE_MAX = 200;

/** The exact text the consent hash covers. */
export function noticeText(payment: PatientPaymentStatus): string {
  return consentHashInput(payment.notice);
}

const card: React.CSSProperties = {
  background: J.white,
  border: `1px solid ${J.line}`,
  borderRadius: J.radiusCard,
  boxShadow: J.shadow,
  padding: "18px 18px 16px",
};
const primary: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  border: 0,
  borderRadius: J.radiusButton,
  background: J.ink,
  color: J.white,
  fontSize: 16,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
const secondary: React.CSSProperties = {
  ...primary,
  background: J.white,
  color: J.ink,
  border: `1px solid ${J.border}`,
};
const linkButton: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: "6px 0",
  color: J.teal,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
};
const disabledStyle = (on: boolean, base: React.CSSProperties): React.CSSProperties =>
  on ? { ...base, background: J.disabled, color: J.white, border: 0, cursor: "not-allowed" } : base;

export default function FeeStage({ payment, onContinue, onDecline, onBack, mode = "fee", pollMs }: FeeStageProps) {
  const [view, setView] = useState<View>("notice");
  // The row's frozen no-fee choices: false (every row sent since the ruling)
  // is the two-choice step; true keeps the four ways out it was sent with.
  const choices = noFeeChoices(payment);
  // A decline the bridge refused (409 `not_declinable`): the confirm screen
  // then says why and offers only Back. "on_its_way": the row still waits
  // on its fee and the form is in or being sent. "changed": the row moved
  // on to something else (the physician closed the request, …).
  const [declineBlocked, setDeclineBlocked] = useState<null | "on_its_way" | "changed">(null);
  const [opened, setOpened] = useState<PayPageOpened | null>(null);
  // Pay-now skips the consent box only when the bridge says it already holds
  // a consent for this notice and amount (`consent_current`). A deferred row
  // released with no card never consented, and without one the bridge
  // refuses setup — so the box shows, and consent is posted first (the
  // bridge's consent route is idempotent).
  const needsConsent = !(mode === "pay" && payment.consent_current === true);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<WaiverReason>("cannot_afford");
  const [note, setNote] = useState("");
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const seller = feeSeller(payment);
  const { doctor, office, contact } = seller;
  const parseo = seller.merchant === "parseo";
  const amount = formatCents(payment.total_cents);

  // Move focus to the heading of each sub-step so a screen reader announces
  // where the patient is.
  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [view]);

  // Synchronous up to openPayPage's window.open: no await may come before it,
  // or the browser treats the new tab as an unrequested popup.
  function continueToPayment() {
    if ((needsConsent && !agreed) || busy) return;
    setBusy(true);
    setError(null);
    openPayPage(async () => {
      if (needsConsent) {
        const hash = await sha256Hex(noticeText(payment));
        const consent = await postPaymentConsent(payment.notice.version, hash, payment.total_cents);
        if (!consent.ok) throw new Error(consent.reason);
      }
      const setup = await postPaymentSetup();
      if (!setup.ok || !setup.body.pay_url) {
        const err = new Error(setup.ok ? "no_pay_url" : setup.reason) as Error & { rowStatus?: string };
        if (!setup.ok) err.rowStatus = setup.rowStatus;
        throw err;
      }
      return setup.body.pay_url;
    }).then(
      (o) => {
        setOpened(o);
        setBusy(false);
        setView("waiting");
      },
      async (e: unknown) => {
        // The bridge refused setup because the row has moved on (409
        // `not_open` with its status). A row already declined is closed.
        // Otherwise the row is read afresh: a card saved in the pay tab
        // after the patient pressed Back is a saved card, not an error; a
        // row the pay page still opens on is a passing failure (the line
        // below); anything else (the physician closed the request while
        // this page was open, …) says the request changed, never "try
        // again" (review of PR #508).
        const rowStatus = (e as { rowStatus?: string } | null)?.rowStatus;
        if (rowStatus === "declined_old_way" && onDecline && mode === "fee") {
          onDecline();
          return;
        }
        if (rowStatus) {
          const r = await fetchPaymentStatus();
          const now = r.kind === "ok" ? String(r.payment.status) : rowStatus;
          if (r.kind === "ok" && SAVED_STATUSES.includes(now)) {
            onContinue("card_saved", now === "paid" ? "" : cardSavedStrip(r.payment), r.payment);
            return;
          }
          if (now === "declined_old_way" && onDecline && mode === "fee") {
            onDecline();
            return;
          }
          if (!PAYABLE_STATUSES.includes(now) && !SAVED_STATUSES.includes(now)) {
            setBusy(false);
            setError(requestChangedLine(payment));
            return;
          }
        }
        setBusy(false);
        setError(
          mode === "pay"
            ? "We couldn't open the payment page just now. Please try again in a moment."
            : choices
              ? "We couldn't open the payment page just now. You can try again, or choose Not now and continue with your form."
              : // DRAFT (2026-10-15, G20).
                "We couldn't open the payment page just now. Please try again in a moment, or choose not to have this form filled out."
        );
      }
    );
  }

  // "Yes, I've decided". Only ever on a row without the no-fee
  // choices, in fee mode. Nothing is recorded unless the bridge says so: a
  // failed post keeps the patient here to try again (a saved card would
  // otherwise still be charged). A refusal on a row still waiting on its fee
  // says the form is already on its way and points to the office; a refusal
  // on any other status re-reads the row and, unless it reads declined, says
  // only that the request changed (review of PR #508: a request the physician
  // closed under an open page is not "on its way").
  async function decline() {
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await postPaymentDecline();
    if (r.ok) {
      if (onDecline) onDecline();
      else setView("notice");
      return;
    }
    if (r.status === 409 && r.reason === "not_declinable") {
      if (r.rowStatus && DECLINABLE_STATUSES.includes(r.rowStatus)) {
        setBusy(false);
        setDeclineBlocked("on_its_way");
        return;
      }
      const now = await fetchPaymentStatus();
      const status = now.kind === "ok" ? String(now.payment.status) : r.rowStatus || "";
      if (status === "declined_old_way" && onDecline) {
        onDecline();
        return;
      }
      setBusy(false);
      setDeclineBlocked(DECLINABLE_STATUSES.includes(status) ? "on_its_way" : "changed");
      return;
    }
    setBusy(false);
    setError(declineFailedLine(seller));
  }

  async function sendWaiver() {
    if (busy) return;
    setBusy(true);
    const r = await postPaymentWaiver(reason, note);
    onContinue(
      "waiver",
      r.ok
        ? `Your form continues. ${office} will see your request.`
        : `Your form continues. We couldn't send your request just now${
            contact ? `; you can ${contact.kind === "email" ? "email" : "call"} ${office} at ${contact.value}` : ""
          }.`
    );
  }

  async function sendQuestion() {
    if (busy) return;
    setBusy(true);
    const r = await postPaymentQuestion(note);
    const call = contactSentence(seller);
    onContinue(
      "question",
      r.ok
        ? `Your form continues. ${office} will see your question.${call}`
        : `Your form continues. We couldn't send your question just now.${call}`
    );
  }

  async function notNow() {
    if (busy) return;
    setBusy(true);
    await postPaymentDefer();
    onContinue("deferred", DEFERRED_MESSAGE);
  }

  if (view === "waiting" && opened) {
    return (
      <PaymentWaiting
        opened={opened}
        pollMs={pollMs}
        onSaved={(latest) =>
          onContinue("card_saved", latest.status === "paid" ? "" : cardSavedStrip(latest), latest)
        }
        leaveLabel={mode === "pay" ? "Not now" : choices ? "Continue without paying now" : "Back"}
        onLeave={
          mode === "pay"
            ? () => (onBack ? onBack() : undefined)
            : choices
              ? notNow
              : // Back to the two choices: never a deferral (no pay-later is offered).
                () => setView("notice")
        }
      />
    );
  }

  if (view === "confirm") {
    return (
      <section aria-labelledby="fee-confirm-heading" data-testid="fee-decline-confirm">
        <h1 id="fee-confirm-heading" ref={headingRef} tabIndex={-1} style={{ fontSize: 22, color: J.ink, margin: "6px 0 8px" }}>
          {DECLINE_CONFIRM_HEADING}
        </h1>
        <p style={{ fontSize: 15, color: J.body, lineHeight: 1.5, margin: 0 }}>{declineConfirmBody(payment)}</p>
        <div aria-live="polite" role="status" style={{ minHeight: 4 }}>
          {declineBlocked ? (
            <p
              data-testid={declineBlocked === "changed" ? "fee-request-changed" : "fee-not-declinable"}
              style={{ fontSize: 14.5, color: J.ink, lineHeight: 1.5, margin: "12px 0 0" }}
            >
              {declineBlocked === "changed" ? requestChangedLine(payment) : notDeclinableLine(payment)}
            </p>
          ) : null}
          {error ? <p style={{ color: "#a33b37", fontSize: 14, margin: "12px 0 0" }}>{error}</p> : null}
        </div>
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          {declineBlocked ? null : (
            <button type="button" style={disabledStyle(busy, primary)} disabled={busy} onClick={decline}>
              {DECLINE_CONFIRM_YES}
            </button>
          )}
          <button
            type="button"
            style={secondary}
            disabled={busy}
            onClick={() => {
              setError(null);
              setView("notice");
            }}
          >
            Back
          </button>
        </div>
      </section>
    );
  }

  const noteField = (label: string) => (
    <label style={{ display: "block", marginTop: 14, fontSize: 14, color: J.body }}>
      {label}
      <textarea
        value={note}
        maxLength={NOTE_MAX}
        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
        rows={3}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          marginTop: 6,
          padding: 10,
          borderRadius: 10,
          border: `1px solid ${J.border}`,
          fontSize: 15,
          fontFamily: "inherit",
        }}
      />
      <span style={{ fontSize: 12, color: J.muted }}>
        {note.length}/{NOTE_MAX}
      </span>
    </label>
  );

  if (view === "waiver") {
    return (
      <section aria-labelledby="fee-waiver-heading" data-testid="fee-waiver">
        <h1 id="fee-waiver-heading" ref={headingRef} tabIndex={-1} style={{ fontSize: 22, color: J.ink, margin: "6px 0 8px" }}>
          I can't pay right now
        </h1>
        <p style={{ fontSize: 15, color: J.body, lineHeight: 1.5, margin: "0 0 12px" }}>
          {parseo ? (
            <>
              Your form will still be completed. {office} decides on the fee. {doctor} reviews and signs your
              form at no charge.
            </>
          ) : (
            <>Your form will still be completed. {office} decides on the fee.</>
          )}
        </p>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend style={{ fontSize: 14, fontWeight: 600, color: J.ink, marginBottom: 6 }}>Reason</legend>
          <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0", fontSize: 15 }}>
            <input
              type="radio"
              name="fee-waiver-reason"
              checked={reason === "cannot_afford"}
              onChange={() => setReason("cannot_afford")}
            />
            I can't afford this fee
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0", fontSize: 15 }}>
            <input
              type="radio"
              name="fee-waiver-reason"
              checked={reason === "other"}
              onChange={() => setReason("other")}
            />
            Other
          </label>
        </fieldset>
        {noteField(`Anything you'd like ${parseo ? office : "the office"} to know (optional)`)}
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          <button type="button" style={disabledStyle(busy, primary)} disabled={busy} onClick={sendWaiver}>
            Send and continue to my form
          </button>
          <button type="button" style={linkButton} onClick={() => setView("notice")}>
            Back
          </button>
        </div>
      </section>
    );
  }

  if (view === "question") {
    return (
      <section aria-labelledby="fee-question-heading" data-testid="fee-question">
        <h1 id="fee-question-heading" ref={headingRef} tabIndex={-1} style={{ fontSize: 22, color: J.ink, margin: "6px 0 8px" }}>
          I have a question
        </h1>
        <p style={{ fontSize: 15, color: J.body, lineHeight: 1.5, margin: 0 }}>
          {office} will see your question, and your form continues in the meantime.
          {contact ? (
            <>
              {" "}You can also {contact.kind === "email" ? `email ${office}` : "call the office"} at{" "}
              <a href={contact.href}>{contact.value}</a>.
            </>
          ) : null}
        </p>
        {noteField("Your question (optional)")}
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          <button type="button" style={disabledStyle(busy, primary)} disabled={busy} onClick={sendQuestion}>
            Send and continue to my form
          </button>
          <button type="button" style={linkButton} onClick={() => setView("notice")}>
            Back
          </button>
        </div>
      </section>
    );
  }

  const blocks = payment.notice?.text_blocks || [];
  const blocked = (needsConsent && !agreed) || busy;
  const isPay = mode === "pay";
  return (
    <section aria-labelledby="fee-heading" data-testid="fee-stage">
      <h1 id="fee-heading" ref={headingRef} tabIndex={-1} style={{ fontSize: 24, color: J.ink, margin: "6px 0 4px" }}>
        {isPay ? "Pay the fee" : "A fee applies to this form"}
      </h1>
      <p style={{ fontSize: 14, color: J.muted, margin: "0 0 14px" }}>
        {needsConsent ? seller.byline : seller.bylineWithoutNotice}
      </p>
      <div style={card}>
        <div style={{ fontSize: 30, fontWeight: 700, color: J.ink }} data-testid="fee-amount">
          {amount} <span style={{ fontSize: 14, fontWeight: 600, color: J.muted }}>CAD</span>
        </div>
        {needsConsent ? (
          <>
            <div data-testid="fee-notice" style={{ marginTop: 10 }}>
              {blocks.map((text, i) => (
                <p key={i} style={{ fontSize: 14.5, color: J.body, lineHeight: 1.55, margin: "0 0 10px" }}>
                  {text}
                </p>
              ))}
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 6, fontSize: 14.5, color: J.ink, lineHeight: 1.45 }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                required
                aria-required="true"
                style={{ marginTop: 3, width: 18, height: 18, flex: "0 0 18px" }}
              />
              <span>{payment.notice?.consent_text}</span>
            </label>
          </>
        ) : (
          <p style={{ fontSize: 14.5, color: J.body, lineHeight: 1.55, margin: "10px 0 0" }}>
            {EARLIER_PAYMENT_FAILED_LINE}
          </p>
        )}
      </div>

      <div aria-live="polite" role="status" style={{ minHeight: 4 }}>
        {error ? <p style={{ color: "#a33b37", fontSize: 14, margin: "12px 0 0" }}>{error}</p> : null}
      </div>

      <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
        {needsConsent && !agreed ? (
          <p id="fee-consent-hint" style={{ fontSize: 13.5, color: J.muted, margin: 0 }}>
            Tick the box above to continue.
          </p>
        ) : null}
        <button
          type="button"
          style={disabledStyle(blocked, primary)}
          disabled={blocked}
          aria-describedby={needsConsent && !agreed ? "fee-consent-hint" : undefined}
          onClick={continueToPayment}
        >
          Continue to payment
        </button>
        {isPay ? (
          <>
            {!choices && payment.declinable === true && onDecline ? (
              // G23(c): the card was declined right before the send, so
              // nothing was charged and the form was not sent; the patient
              // may still decide not to have it filled out.
              <button
                type="button"
                style={secondary}
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setView("confirm");
                }}
              >
                {DECLINE_BUTTON_LABEL}
              </button>
            ) : null}
            <button type="button" style={secondary} disabled={busy} onClick={onBack}>
              Not now
            </button>
          </>
        ) : !choices ? (
          // The decline: never tied to the consent tick (declining needs no
          // agreement to the fee).
          <button
            type="button"
            style={secondary}
            disabled={busy}
            onClick={() => {
              setError(null);
              setView("confirm");
            }}
          >
            {DECLINE_BUTTON_LABEL}
          </button>
        ) : (
          <>
            <button type="button" style={secondary} disabled={busy} onClick={() => { setNote(""); setView("waiver"); }}>
              I can't pay right now
            </button>
            <button type="button" style={secondary} disabled={busy} onClick={() => { setNote(""); setView("question"); }}>
              I have a question
            </button>
            <button type="button" style={secondary} disabled={busy} onClick={notNow}>
              Not now
            </button>
          </>
        )}
      </div>
      {onBack && !isPay ? (
        <div style={{ marginTop: 10 }}>
          <button type="button" style={linkButton} onClick={onBack}>
            Back to my details
          </button>
        </div>
      ) : null}
    </section>
  );
}
